import { useEffect, useMemo, useRef, useState } from "react";
import { createRoom, deleteRoom, getRooms, updateRoom } from "../api";
import ImportExportOverlay from "../components/management/ImportExportOverlay";
import ManagementFilterHeader, { formatManagementSearchInput, matchesManagementSearch, ManagementListLoading, sortManagementRows } from "../components/management/ManagementFilterHeader";
import useIncrementalRows from "../hooks/useIncrementalRows";
import VestaSelect from "../components/common/VestaSelect";
import { OverlayFieldLabel } from "../components/common/OverlayValidation";
import { getUserPageLevel, readStoredUser, UNAUTHORIZED_LABEL } from "../utils/permissions";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/rooms.css";

const EMPTY_ROOM = { site_id: "", room_number: "", room_type_id: "", price: 0, beds: [], services: [] };
function normalizeLookup(value) { return String(value || "").trim().toLowerCase(); }
function resolveCatalogId(value, rows = [], fields = ["description", "code", "_id"]) { const token = normalizeLookup(value); if (!token)
  return ""; return rows.find((row) => fields.some((field) => normalizeLookup(row[field]) === token))?._id || String(value || "").trim(); }
function parseBedImport(value, catalogs = {}) { const text = String(value || ""); if (!text.trim())
  return []; return (catalogs.bedTypes || []).map((bed) => { const description = String(bed.description || ""); if (!description || !text.toLowerCase().includes(description.toLowerCase()))
    return null; const pattern = new RegExp(`(\\d+)\\s*${description.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i"); const quantity = Number(text.match(pattern)?.[1] || 1); return { bed_type_id: bed._id, quantity: Math.max(1, quantity) }; }).filter(Boolean); }
function parseServiceImport(value, catalogs = {}) { const parts = String(value || "").split(/[;,]/).map((item) => normalizeLookup(item)).filter(Boolean); if (!parts.length)
  return []; return (catalogs.roomServices || []).filter((service) => parts.some((part) => normalizeLookup(service.description) === part || normalizeLookup(service.description).includes(part) || part.includes(normalizeLookup(service.description)))).map((service) => ({ room_service_id: service._id })); }
function formatBeds(beds = []) { return (beds || []).filter((bed) => Number(bed.quantity || 0) > 0).map((bed) => `${bed.quantity} ${bed.description || "Letto"}`).join(", "); }
function formatServices(services = []) { return (services || []).map((service) => service.description || "").filter(Boolean).join(", "); }
function makeRoomImportExportFields(catalogs = {}) { return [
  { key: "room_number", label: "Numero", required: true, aliases: ["Camera", "Numero camera"] },
  { key: "room_type_id", label: "Tipologia", required: true, aliases: ["Tipo camera"], parse: (value) => resolveCatalogId(value, catalogs.roomTypes || []), exportValue: (room) => room.room_type || room.room_type_id || "" },
  { key: "site_id", label: "Sede", required: true, parse: (value) => resolveCatalogId(value, catalogs.sites || [], ["description", "code", "_id"]), exportValue: (room) => room.site_description || room.site_id || "" },
  { key: "beds", label: "Letti", parse: (value) => parseBedImport(value, catalogs), exportValue: (room) => formatBeds(room.beds) },
  { key: "services", label: "Dotazioni", aliases: ["Servizi"], parse: (value) => parseServiceImport(value, catalogs), exportValue: (room) => formatServices(room.services) },
]; }
function displayValue(value) {
  const text = String(value || "").trim();
  return text || "-";
}

function formatMaintenanceDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
}

function maintenanceStateLabel(item = {}) {
  if (item.active === false) return "Archiviata";
  const now = new Date();
  const start = item.date_start ? new Date(item.date_start) : null;
  const end = item.date_end ? new Date(item.date_end) : null;
  if (end && !Number.isNaN(end.getTime()) && end < now) return "Conclusa";
  if (start && !Number.isNaN(start.getTime()) && start > now) return "Programmata";
  return "In corso";
}


function getBedIcon(description) {
  const text = String(description || "").toLowerCase();
  if (text.includes("matrimonial") || text.includes("queen") || text.includes("king")) return "🛌";
  if (text.includes("castello")) return "🪜";
  if (text.includes("divano")) return "🛋️";
  if (text.includes("culla") || text.includes("baby")) return "🍼";
  return "🛏️";
}

function getServiceIcon(description) {
  const text = String(description || "").toLowerCase();
  if (text.includes("wifi") || text.includes("wi-fi") || text.includes("internet")) return "📶";
  if (text.includes("tv") || text.includes("televis")) return "📺";
  if (text.includes("aria") || text.includes("clima") || text.includes("condizion")) return "❄️";
  if (text.includes("bagno") || text.includes("doccia")) return "🚿";
  if (text.includes("balcone") || text.includes("terraz")) return "🌿";
  if (text.includes("cassaf")) return "🔐";
  if (text.includes("mini") || text.includes("frigo")) return "🧊";
  if (text.includes("parking") || text.includes("parche")) return "🅿️";
  if (text.includes("colazione")) return "☕";
  if (text.includes("animali") || text.includes("pet")) return "🐾";
  return "✨";
}

function CatalogIcon({ type, description }) {
  return <span className="management_room_icon" aria-hidden="true">{type === "bed" ? getBedIcon(description) : getServiceIcon(description)}</span>;
}

function BedSummary({ beds }) {
  const rows = Array.isArray(beds) ? beds.filter((bed) => Number(bed.quantity || 0) > 0) : [];
  if (!rows.length) return <span>-</span>;
  return (
    <div className="management_room_icon_summary management_bed_icon_summary">
      {rows.map((bed) => (
        <span
          className="management_room_icon_item"
          key={String(bed.bed_type_id || bed.description)}
          data-tooltip={`${bed.quantity} ${bed.description || "Letto"}`}
          aria-label={`${bed.quantity} ${bed.description || "Letto"}`}
        >
          <span className="management_room_icon_qty">{bed.quantity}</span>
          <CatalogIcon type="bed" description={bed.description} />
        </span>
      ))}
    </div>
  );
}

function ServiceSummary({ services }) {
  const rows = Array.isArray(services) ? services : [];
  if (!rows.length) return <span>-</span>;
  return (
    <div className="management_room_icon_summary management_service_icon_summary">
      {rows.map((service) => (
        <span
          className="management_room_icon_item is_service"
          key={String(service.room_service_id || service.description)}
          data-tooltip={service.description || "Dotazione"}
          aria-label={service.description || "Dotazione"}
        >
          <CatalogIcon type="service" description={service.description} />
        </span>
      ))}
    </div>
  );
}

function normalizeFilterValue(value) {
  return String(value || "").trim().toLowerCase();
}

function roomHasBed(room, filterValue) {
  return (room.beds || []).some((bed) => normalizeFilterValue(bed.description) === filterValue && Number(bed.quantity || 0) > 0);
}

function roomHasService(room, filterValue) {
  return (room.services || []).some((service) => normalizeFilterValue(service.description) === filterValue);
}

function RoomOverlay({ draft, catalogs, error, onChange, onBedChange, onServiceToggle, onClose, onSave }) {
  const bedRows = catalogs.bedTypes || [];
  const services = catalogs.roomServices || [];
  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_overlay_card" role="dialog" aria-modal="true" aria-label="Camera">
        <div className="overlay_header">
          <h2>{draft._id ? "MODIFICA CAMERA" : "NUOVA CAMERA"}</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="overlay_body">
          <div className="overlay_section">
            <h3>Dati camera</h3>
            <div className="overlay_fields_grid">
              <label className="overlay_field"><OverlayFieldLabel required>Numero</OverlayFieldLabel><input value={draft.room_number || ""} onChange={(e) => onChange("room_number", e.target.value)} /></label>
              <label className="overlay_field"><OverlayFieldLabel required>Tipologia</OverlayFieldLabel><VestaSelect value={draft.room_type_id || ""} onChange={(e) => onChange("room_type_id", e.target.value)}><option value="">Seleziona</option>{(catalogs.roomTypes || []).map((type) => <option key={type._id} value={type._id}>{type.description}</option>)}</VestaSelect></label>
              <label className="overlay_field"><span>Prezzo</span><input type="number" min="0" step="0.01" value={draft.price ?? 0} onChange={(e) => onChange("price", e.target.value)} /></label>
              <label className="overlay_field"><OverlayFieldLabel required>Sede</OverlayFieldLabel><VestaSelect value={draft.site_id || ""} onChange={(e) => onChange("site_id", e.target.value)}><option value="">Seleziona sede</option>{(catalogs.sites || []).map((site) => <option key={site._id} value={site._id}>{site.description}</option>)}</VestaSelect></label>
            </div>
          </div>
          <div className="overlay_section">
            <h3>Tipologie letto</h3>
            <div className="management_room_option_list">
              {bedRows.map((bed) => {
                const current = (draft.beds || []).find((item) => String(item.bed_type_id) === String(bed._id));
                return (
                  <label className="management_room_option_row" key={bed._id}>
                    <span className="management_room_option_label">
                      <CatalogIcon type="bed" description={bed.description} />
                      <span>{bed.description}</span>
                    </span>
                    <input
                      className="management_room_quantity_input"
                      type="number"
                      min="0"
                      step="1"
                      value={current?.quantity ?? 0}
                      onChange={(e) => onBedChange(bed._id, e.target.value)}
                    />
                  </label>
                );
              })}
            </div>
          </div>
          <div className="overlay_section">
            <h3>Dotazioni disponibili</h3>
            <div className="management_room_option_list">
              {services.map((service) => {
                const checked = (draft.services || []).some((item) => String(item.room_service_id) === String(service._id));
                return (
                  <label className="management_room_option_row" key={service._id}>
                    <span className="management_room_option_label">
                      <CatalogIcon type="service" description={service.description} />
                      <span>{service.description}</span>
                    </span>
                    <input
                      className="management_room_service_check"
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => onServiceToggle(service._id, e.target.checked)}
                    />
                  </label>
                );
              })}
            </div>
          </div>
          {draft._id && (
            <div className="overlay_section">
              <h3>Storico manutenzioni</h3>
              <div className="booking_maintenance_list">
                {(draft.maintenances || []).length === 0 && <div className="booking_maintenance_empty">Nessuna manutenzione inserita</div>}
                {(draft.maintenances || []).slice().sort((a, b) => new Date(b.date_start || 0) - new Date(a.date_start || 0)).map((item) => (
                  <div key={item._id} className="booking_maintenance_item">
                    <div>
                      <strong>{formatMaintenanceDate(item.date_start)} → {formatMaintenanceDate(item.date_end)}</strong>
                      <span>{item.description || "In Manutenzione"} · {maintenanceStateLabel(item)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="overlay_footer"><div className="overlay_error_message">{error}</div><button type="button" className="overlay_save_button overlay_save_close_button" onClick={onSave}>Salva e Chiudi</button></div>
      </div>
    </div>
  );
}

export default function Rooms() {
  const [items, setItems] = useState([]);
  const [catalogs, setCatalogs] = useState({ roomTypes: [], bedTypes: [], roomServices: [], sites: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(EMPTY_ROOM);
  const [overlay, setOverlay] = useState("");
  const [overlayError, setOverlayError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState({ room_number: [], room_type: [], site: [], beds: [], services: [] });
  const [sortState, setSortState] = useState({ key: "room_number", direction: "asc" });
  const [importExportMode, setImportExportMode] = useState("");
  const filterPanelRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const pagePermissionLevel = getUserPageLevel(readStoredUser(), "room");
  const canEditPage = pagePermissionLevel >= 2;
  const canDeletePage = pagePermissionLevel >= 3;
  const blockedActionClass = (allowed) => allowed ? "" : " is_permission_blocked";
  const blockAction = () => {};

  async function load() {
    setLoading(true); setError("");
    try {
      const data = await getRooms();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setCatalogs(data?.catalogs || { roomTypes: [], bedTypes: [], roomServices: [], sites: [] });
    } catch { setError("Impossibile caricare le camere"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!activeFilter) return undefined;
    function closeFilters(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) setActiveFilter("");
    }
    document.addEventListener("pointerdown", closeFilters);
    return () => document.removeEventListener("pointerdown", closeFilters);
  }, [activeFilter]);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    function closeOptions(event) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) {
        setOptionsOpen(false);
        setConfirmBulkDelete(false);
      }
    }
    document.addEventListener("pointerdown", closeOptions);
    return () => document.removeEventListener("pointerdown", closeOptions);
  }, [optionsOpen]);

  useEffect(() => {
    if (selectedRoomIds.length === 0) setConfirmBulkDelete(false);
  }, [selectedRoomIds]);

  const filterOptions = useMemo(() => {
    const makeOptions = (values) => [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, "it", { numeric: true }))
      .map((label) => ({ label, value: normalizeFilterValue(label) }));
    return {
      room_number: makeOptions(items.map((item) => item.room_number)),
      room_type: makeOptions(items.map((item) => item.room_type)),
      site: makeOptions(items.map((item) => item.site_description)),
      beds: makeOptions(items.flatMap((item) => (item.beds || []).filter((bed) => Number(bed.quantity || 0) > 0).map((bed) => bed.description))),
      services: makeOptions(items.flatMap((item) => (item.services || []).map((service) => service.description))),
    };
  }, [items]);
  const roomImportExportFields = useMemo(() => makeRoomImportExportFields(catalogs), [catalogs]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = matchesManagementSearch([
        item.room_number,
        item.room_type,
        item.site_description,
        ...(item.beds || []).map((bed) => bed.description),
        ...(item.services || []).map((service) => service.description),
      ], search);
      if (!matchesSearch) return false;
      if (columnFilters.room_number.length && !columnFilters.room_number.includes(normalizeFilterValue(item.room_number))) return false;
      if (columnFilters.room_type.length && !columnFilters.room_type.includes(normalizeFilterValue(item.room_type))) return false;
      if (columnFilters.site.length && !columnFilters.site.includes(normalizeFilterValue(item.site_description))) return false;
      if (columnFilters.beds.length && !columnFilters.beds.every((value) => roomHasBed(item, value))) return false;
      if (columnFilters.services.length && !columnFilters.services.every((value) => roomHasService(item, value))) return false;
      return true;
    });
  }, [items, search, columnFilters]);

  const roomSortGetters = useMemo(() => ({
    room_number: (room) => room.room_number,
    room_type: (room) => room.room_type,
    site: (room) => room.site_description,
    beds: (room) => formatBeds(room.beds),
    services: (room) => formatServices(room.services),
  }), []);

  const orderedFiltered = useMemo(
    () => sortManagementRows(filtered, sortState, roomSortGetters),
    [filtered, sortState, roomSortGetters]
  );

  function toggleFilter(columnKey) {
    setActiveFilter((current) => (current === columnKey ? "" : columnKey));
  }

  function updateCheckFilter(columnKey, value, checked) {
    setColumnFilters((current) => {
      const values = Array.isArray(current[columnKey]) ? current[columnKey] : [];
      return { ...current, [columnKey]: checked ? [...values, value] : values.filter((item) => item !== value) };
    });
  }

  function clearColumnFilter(columnKey) {
    setColumnFilters((current) => ({ ...current, [columnKey]: Array.isArray(current[columnKey]) ? [] : "" }));
  }

  function toggleSort(columnKey) {
    setSortState((current) => ({
      key: columnKey,
      direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc",
    }));
  }

  function openRoom(item = null) {
    if (!canEditPage) { blockAction(); return; }
    setOverlayError(""); setConfirmDeleteId(""); setOptionsOpen(false); setConfirmBulkDelete(false);
    setDraft({ ...EMPTY_ROOM, ...(item || {}), beds: item?.beds || [], services: item?.services || [] });
    setOverlay("room");
  }

  function updateDraft(field, value) {
    setDraft((current) => {
      if (field === "room_type_id") {
        const selectedType = (catalogs.roomTypes || []).find((item) => String(item._id) === String(value));
        return { ...current, room_type_id: value, price: Number(selectedType?.defaultPrice || 0) };
      }
      return { ...current, [field]: value };
    });
  }
  function updateBed(bedTypeId, quantity) {
    setDraft((current) => {
      const beds = (current.beds || []).filter((item) => String(item.bed_type_id) !== String(bedTypeId));
      const n = Math.max(0, Math.floor(Number(quantity || 0)));
      if (n > 0) beds.push({ bed_type_id: bedTypeId, quantity: n });
      return { ...current, beds };
    });
  }
  function toggleService(serviceId, checked) {
    setDraft((current) => {
      const services = (current.services || []).filter((item) => String(item.room_service_id) !== String(serviceId));
      if (checked) services.push({ room_service_id: serviceId });
      return { ...current, services };
    });
  }

  async function saveRoom() {
    if (!canEditPage) { setOverlayError(UNAUTHORIZED_LABEL); return; }
    setOverlayError("");
    if (!draft.room_number || !draft.room_type_id || !draft.site_id) return setOverlayError("Completa i Dati Richiesti");
    try {
      const data = draft._id ? await updateRoom(draft._id, draft) : await createRoom(draft);
      const saved = data?.item;
      if (saved) setItems((current) => current.some((item) => item._id === saved._id) ? current.map((item) => item._id === saved._id ? saved : item) : [...current, saved]);
      setOverlay("");
    } catch (err) { setOverlayError(err?.response?.data?.error || "Dati Errati"); }
  }

  function openImportExport(mode) {
    if (mode === "import" && !canEditPage) { blockAction(); return; }
    setImportExportMode(mode);
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
  }

  async function importRooms(records) {
    if (!canEditPage) throw new Error(UNAUTHORIZED_LABEL);
    let imported = 0;
    for (const record of records) {
      try {
        const data = await createRoom({ ...EMPTY_ROOM, ...record });
        const saved = data?.item;
        if (saved) setItems((current) => current.some((item) => item._id === saved._id) ? current.map((item) => item._id === saved._id ? saved : item) : [...current, saved]);
        imported += 1;
      } catch (err) {
        throw new Error(`Riga ${imported + 2}: ${err?.response?.data?.error || "Dati Errati"}`);
      }
    }
    return { imported };
  }

  function toggleSelectedRoom(roomId, checked) {
    setSelectedRoomIds((current) => checked ? [...new Set([...current, roomId])] : current.filter((id) => String(id) !== String(roomId)));
  }

  function toggleAllFilteredRooms(checked) {
    const filteredIds = orderedFiltered.map((room) => room._id);
    setSelectedRoomIds((current) => {
      if (checked) return [...new Set([...current, ...filteredIds])];
      return current.filter((id) => !filteredIds.some((roomId) => String(roomId) === String(id)));
    });
  }

  function resetSearchAndFilters() {
    setSearch("");
    setColumnFilters({ room_number: [], room_type: [], site: [], beds: [], services: [] });
    setActiveFilter("");
    setSortState({ key: "room_number", direction: "asc" });
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
  }

  async function removeRoom(room) {
    if (!canDeletePage) { blockAction(); setConfirmDeleteId(""); return; }
    try {
      await deleteRoom(room._id);
      setItems((current) => current.filter((item) => item._id !== room._id));
      setSelectedRoomIds((current) => current.filter((id) => String(id) !== String(room._id)));
      setConfirmDeleteId("");
    } catch (err) { setError(err?.response?.data?.error || "Impossibile eliminare la camera"); }
  }

  async function removeSelectedRooms() {
    if (selectedRoomIds.length === 0) return;
    if (!canDeletePage) { blockAction(); setConfirmBulkDelete(false); return; }
    setError("");
    try {
      await Promise.all(selectedRoomIds.map((roomId) => deleteRoom(roomId)));
      setItems((current) => current.filter((item) => !selectedRoomIds.some((roomId) => String(roomId) === String(item._id))));
      setSelectedRoomIds([]);
      setConfirmBulkDelete(false);
      setOptionsOpen(false);
    } catch (err) { setError(err?.response?.data?.error || "Impossibile eliminare le camere selezionate"); }
  }

  const filteredRoomIds = orderedFiltered.map((room) => room._id);
  const allFilteredSelected = filteredRoomIds.length > 0 && filteredRoomIds.every((roomId) => selectedRoomIds.some((id) => String(id) === String(roomId)));
  const someFilteredSelected = filteredRoomIds.some((roomId) => selectedRoomIds.some((id) => String(id) === String(roomId)));
  const incrementalRows = useIncrementalRows(orderedFiltered);

  return (
    <section className="window_page bookings_page management_page rooms_page">
      <div className="window_banner"><div className="window_banner_copy"><h1>CAMERE</h1><p>Gestione camere, collegamento sedi, tipologie, letti, dotazioni e blocchi manutenzione.</p></div></div>
      <div className="management_list_panel">
        <div className="booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_rooms_toolbar">
          <div className="management_options_menu" ref={optionsMenuRef}>
            <button type="button" className="management_options_button" onClick={() => setOptionsOpen((current) => !current)} aria-label="Opzioni camere" aria-expanded={optionsOpen}>
              <span></span><span></span><span></span>
            </button>
            {optionsOpen && (
              <div className="management_options_dropdown">
                <button type="button" className={`management_options_item${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openRoom()}>Nuova Camera</button>
                <button type="button" className="management_options_item" onClick={resetSearchAndFilters}>Azzera ricerca e filtri</button>
                <button type="button" className={`management_options_item${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openImportExport("import")}>Importa Valori</button>
                <button type="button" className="management_options_item" onClick={() => openImportExport("export")}>Esporta Valori</button>
                {confirmBulkDelete ? (
                  <div className="management_options_confirm_row">
                    <button type="button" className="management_options_item is_neutral" onClick={() => setConfirmBulkDelete(false)}>Annulla</button>
                    <button type="button" className={`management_options_item is_danger${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canDeletePage ? removeSelectedRooms : blockAction}>Conferma</button>
                  </div>
                ) : (
                  <button type="button" className={`management_options_item is_danger${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={selectedRoomIds.length === 0} onClick={() => canDeletePage ? setConfirmBulkDelete(true) : blockAction()}>Eliminazione Multipla</button>
                )}
              </div>
            )}
          </div>
          <input className="management_search" value={search} onChange={(e) => setSearch(formatManagementSearchInput(e.target.value))} placeholder="Cerca camere (es. 101, 202)" />
          <div className="management_toolbar_results">
            <strong className="management_results_count">Risultati: {filtered.length}</strong>
            
          </div>
        </div>
        <div className="management_board management_table_board">
          <div className="management_table_shell" role="table" aria-label="Elenco camere" ref={incrementalRows.shellRef} onScroll={incrementalRows.handleScroll}>
            <div className="management_table_header management_rooms_table_header" role="row" ref={filterPanelRef}>
              <div className="management_table_select_head" role="columnheader">
                <input type="checkbox" checked={allFilteredSelected} ref={(input) => { if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected; }} onChange={() => toggleAllFilteredRooms(!someFilteredSelected)} aria-label="Seleziona tutte le camere filtrate" />
              </div>
              <ManagementFilterHeader columnKey="room_number" label="Numero" filterType="checks" options={filterOptions.room_number} value={columnFilters.room_number} activeFilter={activeFilter} onToggle={toggleFilter} onCheckChange={updateCheckFilter} onClear={clearColumnFilter} sortable sortDirection={sortState.key === "room_number" ? sortState.direction : ""} onSort={toggleSort} />
              <ManagementFilterHeader columnKey="room_type" label="Tipologia" filterType="checks" options={filterOptions.room_type} value={columnFilters.room_type} activeFilter={activeFilter} onToggle={toggleFilter} onCheckChange={updateCheckFilter} onClear={clearColumnFilter} sortable sortDirection={sortState.key === "room_type" ? sortState.direction : ""} onSort={toggleSort} />
              <ManagementFilterHeader columnKey="site" label="Sede" filterType="checks" options={filterOptions.site} value={columnFilters.site} activeFilter={activeFilter} onToggle={toggleFilter} onCheckChange={updateCheckFilter} onClear={clearColumnFilter} sortable sortDirection={sortState.key === "site" ? sortState.direction : ""} onSort={toggleSort} />
              <ManagementFilterHeader columnKey="beds" label="Letti" filterType="checks" options={filterOptions.beds} value={columnFilters.beds} activeFilter={activeFilter} onToggle={toggleFilter} onCheckChange={updateCheckFilter} onClear={clearColumnFilter} sortable sortDirection={sortState.key === "beds" ? sortState.direction : ""} onSort={toggleSort} />
              <ManagementFilterHeader columnKey="services" label="Dotazioni" filterType="checks" options={filterOptions.services} value={columnFilters.services} activeFilter={activeFilter} onToggle={toggleFilter} onCheckChange={updateCheckFilter} onClear={clearColumnFilter} sortable sortDirection={sortState.key === "services" ? sortState.direction : ""} onSort={toggleSort} />
              <div className="management_table_actions_head" role="columnheader">Azioni</div>
            </div>
            {loading && <ManagementListLoading label="Caricamento Camere" />}{!loading && error && <div className="management_table_visible_status is_error">{error}</div>}{!loading && !error && filtered.length === 0 && <div className="management_table_visible_status">Nessun Risultato Trovato</div>}<div className="management_table_body" role="rowgroup">
              
              
              {incrementalRows.rows.map((room) => {
                const isConfirming = String(confirmDeleteId) === String(room._id);
                return (
                  <article key={room._id} className={`management_table_row management_rooms_table_row${selectedRoomIds.some((id) => String(id) === String(room._id)) ? " is_selected" : ""}`} data-vesta-focus-id={room._id} role="row">
                    <div className="management_table_cell management_table_select_cell" role="cell">
                      <input type="checkbox" checked={selectedRoomIds.some((id) => String(id) === String(room._id))} onChange={(event) => toggleSelectedRoom(room._id, event.target.checked)} aria-label={`Seleziona camera ${room.room_number}`} />
                    </div>
                    <div className="management_table_cell management_table_title_cell" role="cell"><strong>{room.room_number}</strong></div>
                    <div className="management_table_cell" role="cell">{displayValue(room.room_type)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(room.site_description)}</div>
                    <div className="management_table_cell management_table_icon_cell" role="cell">
                      <BedSummary beds={room.beds} />
                    </div>
                    <div className="management_table_cell management_table_icon_cell" role="cell">
                      <ServiceSummary services={room.services} />
                    </div>
                    <div className="management_table_actions" role="cell">
                      {isConfirming ? (
                        <>
                          <button type="button" className="management_action_button management_action_cancel" onClick={() => setConfirmDeleteId("")}>Annulla</button>
                          <button type="button" className={`management_action_button management_action_confirm${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => removeRoom(room)}>Conferma</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className={`management_action_button management_action_primary${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openRoom(room)}>Modifica</button>
                          <button type="button" className={`management_action_button management_action_danger${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => canDeletePage ? setConfirmDeleteId(room._id) : blockAction()}>Elimina</button>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {overlay === "room" && <RoomOverlay draft={draft} catalogs={catalogs} error={overlayError} onChange={updateDraft} onBedChange={updateBed} onServiceToggle={toggleService} onClose={() => setOverlay("")} onSave={saveRoom} />}
      {importExportMode && <ImportExportOverlay mode={importExportMode} title="Camere" entityLabel="Camere" fileBaseName="vesta_camere" fields={roomImportExportFields} rows={orderedFiltered} onClose={() => setImportExportMode("")} onImport={importRooms} />}
    </section>
  );
}
